import { api } from '@connected/api-client';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { logger } from '@/lib/logger';

export function useUserSignature({ userId, enabled = true }: { userId: string; enabled?: boolean }) {
    const { t } = useTranslation('dashboard');

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [updatedFile, setUpdatedFile] = useState<boolean>(false);
    const [signatureLoaded, setSignatureLoaded] = useState<boolean>(false);
    const [signatureRemoved, setSignatureRemoved] = useState<boolean>(false);

    const fetchValidation = useQuery(portaalQueryOptions.electrical.users.signatureValidation());

    const fetchFiles = useQuery({
        ...portaalQueryOptions.electrical.users.userFiles({ userId }),
        enabled: enabled && !!userId,
    });

    const validation = useMemo(() => {
        if (fetchValidation.isSuccess)
            return fetchValidation.data;
        return null;
    }, [fetchValidation.data, fetchValidation.isSuccess]);

    const findPictureFile = useMemo(() => {
        if (fetchFiles.isSuccess && fetchFiles.isFetched) {
            const file = fetchFiles.data.Files.find(f => f.Description === 'userSignature');
            if (file) {
                return file;
            }
            // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
            setSignatureLoaded(true);
            return null;
        }
        return null;
    }, [fetchFiles.data?.Files, fetchFiles.isFetched, fetchFiles.isSuccess]);

    const fetchSelectedFile = useQuery({
        ...portaalQueryOptions.electrical.users.downloadUserFile({
            userFileId: findPictureFile?.UserFileId ?? '',
        }),
        enabled: !!findPictureFile?.UserFileId,
    });

    useEffect(() => {
        if (enabled && fetchSelectedFile.isSuccess && fetchSelectedFile.data && !selectedFile && !signatureRemoved) {
            try {
                const blob = fetchSelectedFile.data as Blob;
                const file = new File([blob], 'signature.png', { type: blob.type || 'image/png' });
                // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
                setSelectedFile(file);
                // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
                setSignatureLoaded(true);
            }
            catch (error) {
                logger.error(
                    { error, system: 'LoadSignature' },
                    { apiHttpMethod: 'Get', apiUrl: '/api/v2/users/files/download' },
                );
                // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
                setSignatureLoaded(true);
            }
        }
        else if (enabled && fetchFiles.isFetched && !findPictureFile && !signatureLoaded) {
            // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
            setSignatureLoaded(true);
        }
    }, [enabled, fetchSelectedFile.isSuccess, fetchSelectedFile.data, fetchFiles.isFetched, findPictureFile, selectedFile, signatureLoaded, signatureRemoved]);

    const removeSignatureMutation = api.useMutation('delete', '/api/v2/users/files/delete', {
        onError: (error) => {
            logger.error(
                { system: 'DeleteUserFileCommandResult', error },
                { apiHttpMethod: 'Delete', apiUrl: '/api/v2/users/files/delete' },
            );
            toast.error(error?.Message ?? t('dashboard.general.messages.error'));
        },
    });

    const uploadSignatureMutation = api.useMutation('post', '/api/v2/users/files/signatures/create', {
        onError: (error) => {
            logger.error(
                { system: 'UploadFileCommandResult', error },
                { apiHttpMethod: 'Post', apiUrl: '/api/v2/users/files/signatures/create' },
            );
            toast.error(error?.Message ?? t('dashboard.general.messages.error'));
        },
    });

    async function handleSignatureSubmit(targetUserId: string) {
        if (!selectedFile && signatureRemoved && findPictureFile?.UserFileId) {
            await removeSignatureMutation.mutateAsync({
                params: { query: { UserFileId: findPictureFile.UserFileId } },
            });
        }

        if (selectedFile && updatedFile) {
            await uploadSignatureMutation.mutateAsync({
                params: {
                    query: { UserId: targetUserId, Description: 'userSignature' },
                },
                body: { File: '' },
                bodySerializer: () => {
                    const formData = new FormData();
                    formData.append('File', selectedFile as Blob);
                    return formData;
                },
            });
        }
    }

    function resetState() {
        setSelectedFile(null);
        setUpdatedFile(false);
        setSignatureLoaded(false);
        setSignatureRemoved(false);
    }

    return {
        selectedFile,
        setSelectedFile,
        updatedFile,
        setUpdatedFile,
        signatureLoaded,
        signatureRemoved,
        setSignatureRemoved,
        validation,
        findPictureFile,
        handleSignatureSubmit,
        resetState,
        isLoading: fetchFiles.isLoading || fetchSelectedFile.isLoading || fetchValidation.isLoading,
    };
}
